<template>
    <div>
        <LoadingSpinner v-if="loading" />
        <template v-else>
            <svg-sprite />
            <transition
                v-if="!showMobilePrompt"
                name="fade"
                mode="out-in"
            >
                <router-view :class="$store.state.dir" />
            </transition>
            <main
                v-else
                class="container min-full d-flex flex-column justify-content-center px-3 py-4 text-center"
            >
                <h1 class="mb-3">
                    {{ $t('mobilePromptTitle') }}
                </h1>
                <p class="mb-4">
                    {{ $t('mobilePromptText') }}
                </p>
                <b-button
                    variant="primary"
                    :href="mobilePromptUrl"
                >
                    {{ $t('mobilePromptButton') }}
                </b-button>
            </main>
            <b-modal
                title="Maintenance Mode"
                :visible="$store.getters.getMaintenanceMode"
                no-close-on-backdrop
                no-close-on-esc
                hide-header-close
                hide-footer
                centered
                header-class="justify-content-center"
            >
                <p class="text-center mb-0">
                    {{ $store.getters.getMaintenanceModeMessage }}
                </p>
            </b-modal>
            <b-modal
                :title="$t('updateTitle')"
                :visible="updateExists && !$device.mobile"
                :ok-title="$t('updateButton')"
                no-close-on-backdrop
                no-close-on-esc
                hide-header-close
                ok-only
                centered
                header-class="justify-content-center"
                title-class="w-100 text-center"
                body-class="text-center"
                footer-class="justify-content-center"
                @ok="refreshApp"
                >{{ $t('updateText') }}</b-modal
            >
            <b-toast
                toaster="b-toaster-bottom-right"
                :visible="!$device.windows && installable && !$store.getters.getMaintenanceMode && !updateExists"
                :title="$t('installPromptTitle')"
                variant="primary"
                solid
            >
                <p>{{ $t('installPromptText') }}</p>
                <b-button
                    variant="primary"
                    @click="installApp"
                    >{{ $t('installPromptButton') }}</b-button
                >
            </b-toast>
            <b-toast
                toaster="b-toaster-bottom-right"
                :visible="$device.windows && !$device.mobile && !fromMsStore && $store.state.site.microsoftStore && !$store.getters.getMaintenanceMode && !updateExists"
                :title="$t('installPromptTitle')"
                variant="primary"
                solid
            >
                <p>{{ $t('installPromptMicrosoft') }}</p>
                <b-button
                    variant="primary"
                    :href="$store.state.site.microsoftStore"
                    target="_blank"
                    >{{ $t('installPromptMicrosoftButton') }}</b-button
                >
            </b-toast>
            <b-toast
                toaster="b-toaster-bottom-right"
                :visible="!$device.windows && !$device.mobile && !compatBrowser && !$store.getters.getMaintenanceMode && !updateExists"
                :title="$t('browserPopupTitle')"
                variant="primary"
                solid
            >
                <p>{{ $t('browserPopupText') }}</p>
            </b-toast>
            <b-toast
                toaster="b-toaster-bottom-right"
                :visible="$store.getters.getCourseRequestQueue.length > 0"
                :title="$t('syncCourseTitle')"
                variant="primary"
                solid
            >
                <p>{{ $t('syncCourseText') }}</p>
            </b-toast>
        </template>
    </div>
</template>

<script>
import SvgSprite from '@/components/SvgSprite';
import AuthService from '@/services/AuthService.js';
import LoadingSpinner from '@/components/loader/Loader';
import { logoutToLogin } from '@/common/auth';
import { notifyError } from '@/common/network';

export default {
    components: {
        SvgSprite,
        LoadingSpinner,
    },
    data() {
        return {
            refreshing: false,
            registration: null,
            updateExists: false,
            installable: false,
            deferredPrompt: null,
            loading: true,
        };
    },
    computed: {
        compatBrowser() {
            if ('mediaDevices' in navigator && 'getScreenDetails' in window && 'wakeLock' in navigator) {
                return true;
            }
            return false;
        },
        fromMsStore() {
            if (document.referrer === 'app-info://platform/microsoft-store' || sessionStorage.getItem('is_ms_store_pwa') === 'true') {
                sessionStorage.setItem('is_ms_store_pwa', 'true');
                return true;
            }
            return false;
        },
        mobilePromptUrl() {
            if (this.$device.ios || this.$device.ipad) {
                return this.$store.state.site.appleAppStore;
            }

            return this.$store.state.site.googlePlayStore;
        },
        showMobilePrompt() {
            const isIosDevice = this.$device.ios || this.$device.ipad;
            const isAndroidDevice = this.$device.android || (this.$device.mobile && !isIosDevice);
            const hasIosStoreLink = Boolean(this.$store.state.site.appleAppStore);
            const hasAndroidStoreLink = Boolean(this.$store.state.site.googlePlayStore);

            if (this.fromMsStore) {
                return false;
            }

            if (isIosDevice) {
                return hasIosStoreLink;
            }

            if (isAndroidDevice) {
                return hasAndroidStoreLink;
            }

            return false;
        },
    },
    async created() {
        try {
            await this.$store.dispatch('setSite', window.location.host);

            this.$watch(
                '$route',
                (to) => {
                    document.title = to.meta.title ? to.meta.title + ' | ' + this.$store.state.site.shortTitle : this.$store.state.site.shortTitle;
                },
                { immediate: true }
            );

            var id = this.$store.state.site.id;
            const icons = [
                { id: 'icon', path: `/img/${id}/icons/favicon.ico` },
                { id: 'manifest', path: `/manifests/${id}.json` },
                { id: 'icon16', path: `/img/${id}/icons/icon-16.png` },
                { id: 'icon32', path: `/img/${id}/icons/icon-32.png` },
                { id: 'appleIcon', path: `/img/${id}/icons/icon-180.png` },
                {
                    id: 'maskIcon',
                    path: `/img/${id}/icons/icon.svg`,
                    color: this.$store.state.site.styles.colors.primary,
                },
            ];
            icons.forEach(({ id, path, color }) => {
                const el = document.getElementById(id);
                if (el) {
                    el.href = path;
                    if (color) {
                        el.setAttribute('color', color);
                    }
                }
            });

            this.styleTag = document.createElement('style');
            this.styleTag.textContent = `
            @font-face {
                font-family: ${this.$store.state.site.styles.font};
                font-weight: 100 900;
                font-style: normal;
                src: url('/fonts/${this.$store.state.site.styles.font.toLowerCase().replace(/\s+/g, '')}.ttf') format("truetype");
            }
            :root {
                --primary: ${this.$store.state.site.styles.colors.primary};
                --contrast: ${this.$store.state.site.styles.colors.contrast};
                --shadow: ${this.$store.state.site.styles.colors.shadow};
                --darker: ${this.$store.state.site.styles.colors.darker};
                --font-family: '${this.$store.state.site.styles.font}', sans-serif;
            }
            body::before {
                background: ${this.$store.state.site.styles.backgroundStyle};
                opacity: ${this.$store.state.site.styles.backgroundOpacity};
            }
            .b-sidebar > .b-sidebar-body:before {
                background: ${this.$store.state.site.styles.sidebarStyle};
                opacity: ${this.$store.state.site.styles.sidebarOpacity};
            }
            `;
            document.head.appendChild(this.styleTag);
            if (this.showMobilePrompt) {
                if (this.$store.getters.getToken) {
                    await logoutToLogin();
                }
                this.loading = false;
                return;
            }
            if (this.$store.state.site.id && this.$store.state.site.id !== 0) {
                const response = await AuthService.settings();
                if (response.success && response.settings) {
                    this.$store.dispatch('setSettings', response.settings);
                } else {
                    throw new Error('Settings not found');
                }
            } else {
                this.$store.dispatch('setSettings', {
                    must_interact: true,
                    photos: false,
                    screenshots: false,
                    secure_assessments: false,
                    timeout: 30,
                    ai: false
                });
            }
            this.loading = false;
        } catch (error) {
            notifyError(error, {
                source: {
                    screen: 'App',
                    action: 'created.settings',
                    siteId: this.$store.state.site?.id,
                },
            });
            this.loading = false;
        }

        document.addEventListener('swUpdated', this.updateAvailable, {
            once: true,
        });
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                if (!this.refreshing) return;
                window.location.reload();
            });
        }
        window.addEventListener('beforeinstallprompt', (e) => {
            e.preventDefault();
            this.deferredPrompt = e;
            this.installable = true;
        });
        window.addEventListener('appinstalled', () => {
            this.deferredPrompt = null;
        });
    },
    methods: {
        updateAvailable(event) {
            if (this.$route.meta.update && !this.$store.getters.getMaintenanceMode) {
                this.registration = event.detail;
                this.updateExists = true;
            }
        },
        refreshApp() {
            this.updateExists = false;
            if (!this.registration || !this.registration.waiting) {
                window.location.reload();
                return;
            }
            this.refreshing = true;
            this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        },
        async installApp() {
            const promptEvent = this.deferredPrompt;
            if (!promptEvent) {
                return;
            }
            promptEvent.prompt();
            this.deferredPrompt = null;
            this.installable = false;
        },
    },
};
</script>
